<template>
  <div class="ai-settings">
    <!-- Title, state and the switch on one line, above the tabs rather than inside one:
         the kill switch is the reason someone opens this during an incident, and it must
         not be one tab-click away from whoever is holding the phone. -->
    <header class="ai-settings__header">
      <h1 class="ai-settings__title">AI Workspace Setting</h1>

      <span v-if="canManageConfig" class="ai-settings__pill" :class="statusPill.variant">{{ statusPill.label }}</span>

      <!-- The theme's own switch, as used across ARCH, rather than a checkbox.
           `:checked` rather than `v-model`: turning it off asks first, and the switch
           must not sit in the off position while that question is still open.

           Absent for someone who cannot move it. A kill switch is not a status readout,
           and one that refuses every click is worse than no switch at all. -->
      <div v-if="canManageConfig" class="ai-settings__header-switch custom-switch success-handle">
        <input
          id="ai-chat-enabled"
          type="checkbox"
          class="custom-switch-input"
          :checked="chatEnabledSwitch"
          :disabled="!config || togglingChat"
          @change="onSwitchToggled"
        />
        <label class="custom-switch-label" for="ai-chat-enabled">
          <span class="custom-switch-text1">On</span>
          <span class="custom-switch-text2">Off</span>
        </label>
      </div>

      <span v-if="togglingChat" class="ai-settings__pending">
        <i class="fa fa-spinner fa-spin"></i>
        {{ chatEnabledSwitch ? "Switching on…" : "Switching off…" }}
      </span>
    </header>

    <p v-if="canManageConfig && config && !availability.on" class="ai-settings__down">
      <i class="fa fa-exclamation-circle mr-1"></i>{{ availability.detail }}
    </p>

    <div v-if="loadError" class="alert alert-danger">
      {{ loadError.message }}
      <span v-if="loadError.code" class="small text-muted ml-1">{{ loadError.code }}</span>
      <button type="button" class="btn btn-sm btn-outline-danger ml-2" @click="loadAll">Retry</button>
    </div>

    <!-- Tabs rather than one long scroll: these are four unrelated jobs — flip the
         switch, tune the workspace numbers, edit a profile, decide who is in the pilot —
         and stacked, three of them had to be scrolled past to reach the fourth.
         Availability leads, and stays beside the settings it explains, because the kill
         switch is what someone opening this at 11pm came for. -->
    <b-tabs class="ai-settings__tabs" content-class="pt-3">
      <b-tab v-if="canManageConfig" title="Model" active>
        <!-- ── Settings ───────────────────────────────────────────────────────────────
             Labels carry the explanation on an info icon rather than a line of help text
             under every field. Eight descriptions stacked down the form pushed the fields
             themselves apart until the shape of the form was hard to read; the words are
             still there for anyone who wants them, one hover away. -->
        <div class="row clearfix">
          <div class="col-lg-12">
            <div class="card card-top-line">
              <div class="body">
                <div v-if="configLoading" class="text-muted py-3">
                  <i class="fa fa-spinner fa-spin mr-1"></i> Loading settings…
                </div>

                <div v-else-if="config" class="form-row">
                  <div class="col-md-12 form-group">
                    <label class="ai-settings__label" for="cfg-model">
                      MODEL
                      <i
                        v-b-tooltip.hover
                        class="fa fa-info-circle ai-settings__info"
                        title="The assistant that answers every message. Only models cleared for use are listed."
                      ></i>
                    </label>
                    <select id="cfg-model" v-model="form.defaultModelId" class="form-control ai-settings__model">
                      <!-- Disabled models are still listed. The current default may be one of
                           them, and a select that dropped it silently would show a blank
                           where the model actually in use should be. -->
                      <option v-for="model in models" :key="model.modelId" :value="model.modelId" :disabled="!model.isEnabled && model.modelId !== config.defaultModelId">
                        {{ model.displayName }}{{ model.isEnabled ? "" : " (disabled)" }}
                      </option>
                    </select>
                    <!-- Kept, unlike the descriptions: this one is a live fault. Every
                         message fails while it holds, and nothing else on the screen
                         would say why. -->
                    <!-- What the chosen model costs, stated rather than asked for. A
                         rate is a fact about the provider's price list, and a box an
                         administrator can type into invites a number nobody checked —
                         which every spend limit would then be measured against. -->
                    <p class="ai-settings__rate mb-0">
                      <template v-if="modelIsPriced">
                        ${{ formatMicros(selectedModel.inputPricePerMillionTokens) }} in ·
                        ${{ formatMicros(selectedModel.outputPricePerMillionTokens) }} out
                        per 1M tokens
                        <!-- Budgets are projected at the higher of the two, because a
                             token count cannot be costed without knowing its input and
                             output mix, and over-estimating is the safe direction for a
                             budget. Charging uses the real split rates per call. -->
                        <i
                          v-b-tooltip.hover
                          class="fa fa-info-circle ai-settings__info"
                          title="Recorded from the provider's price list, not set here. Spend limits are projected at the higher of the two rates, so a budget can only be reached early rather than late; actual charging uses each rate for its own half."
                        ></i>
                      </template>
                      <template v-else>
                        <i class="fa fa-exclamation-circle mr-1"></i>No rate is recorded for
                        this model, so spend limits cannot bind.
                      </template>
                    </p>

                    <div v-if="currentModelDisabled" class="text-danger small mt-1">
                      The model in use has been disabled. Messages will fail until another
                      one is chosen.
                    </div>
                  </div>

                  <div class="col-md-3 form-group">
                    <label class="ai-settings__label" for="cfg-context-tokens">
                      MAX CONTEXT TOKENS
                      <i
                        v-b-tooltip.hover
                        class="fa fa-info-circle ai-settings__info"
                        title="How much of a conversation the assistant re-reads before it answers. Higher means it holds more of the thread in mind, and every message costs more."
                      ></i>
                    </label>
                    <input id="cfg-context-tokens" v-model.number="form.maxContextTokens" type="number" min="1000" max="1000000" class="form-control" />
                    <div v-if="errors.maxContextTokens" class="text-danger small mt-1">{{ errors.maxContextTokens }}</div>
                  </div>

                  <div class="col-md-3 form-group">
                    <label class="ai-settings__label" for="cfg-context-turns">
                      MAX CONTEXT TURNS
                      <i
                        v-b-tooltip.hover
                        class="fa fa-info-circle ai-settings__info"
                        title="The same limit counted in messages rather than length. Whichever runs out first is the one that applies."
                      ></i>
                    </label>
                    <input id="cfg-context-turns" v-model.number="form.maxContextTurns" type="number" min="1" max="500" class="form-control" />
                    <div v-if="errors.maxContextTurns" class="text-danger small mt-1">{{ errors.maxContextTurns }}</div>
                  </div>

                  <div class="col-md-3 form-group">
                    <label class="ai-settings__label" for="cfg-output">
                      MAX OUTPUT TOKENS
                      <i
                        v-b-tooltip.hover
                        class="fa fa-info-circle ai-settings__info"
                        title="The longest single answer anyone can get. A quota profile asking for more is cut down to this."
                      ></i>
                    </label>
                    <input id="cfg-output" v-model.number="form.maxOutputTokens" type="number" min="256" max="128000" class="form-control" />
                    <div v-if="errors.maxOutputTokens" class="text-danger small mt-1">{{ errors.maxOutputTokens }}</div>
                  </div>

                  <div class="col-md-3 form-group">
                    <label class="ai-settings__label" for="cfg-summary">
                      SUMMARY TRIGGER TOKENS
                      <i
                        v-b-tooltip.hover
                        class="fa fa-info-circle ai-settings__info"
                        title="Once a conversation grows this far past its last summary, the older turns are condensed so the thread can carry on without losing the plot."
                      ></i>
                    </label>
                    <input id="cfg-summary" v-model.number="form.summaryTriggerTokens" type="number" min="1000" max="500000" class="form-control" />
                    <div v-if="errors.summaryTriggerTokens" class="text-danger small mt-1">{{ errors.summaryTriggerTokens }}</div>
                  </div>

                  <div class="col-md-3 form-group">
                    <label class="ai-settings__label" for="cfg-ceiling">
                      MONTHLY SPEND CEILING
                      <i
                        v-b-tooltip.hover
                        class="fa fa-info-circle ai-settings__info"
                        title="The most the organisation should spend on AI in a calendar month, in whole currency units. Leave it empty for no ceiling at all."
                      ></i>
                    </label>
                    <!-- The unit named on the field rather than left to the tooltip: this
                         is the one number here that is money, and a bare 300 beside a
                         column of token counts reads as another token count. -->
                    <div class="input-group">
                      <div class="input-group-prepend">
                        <span class="input-group-text">$</span>
                      </div>
                      <input id="cfg-ceiling" v-model="form.spendCeilingUnits" type="number" min="0" step="0.01" class="form-control" placeholder="No ceiling" />
                    </div>
                    <div v-if="errors.spendCeilingUnits" class="text-danger small mt-1">{{ errors.spendCeilingUnits }}</div>
                  </div>

                  <div class="col-md-3 form-group">
                    <label class="ai-settings__label" for="cfg-thresholds">
                      SPEND ALERT THRESHOLDS
                      <i
                        v-b-tooltip.hover
                        class="fa fa-info-circle ai-settings__info"
                        title="How far into the monthly ceiling to raise an alert — 80 means warn once four fifths of it is gone."
                      ></i>
                    </label>
                    <input id="cfg-thresholds" v-model="form.thresholdsText" type="text" class="form-control" placeholder="50, 80, 95" />
                    <div v-if="errors.thresholdsText" class="text-danger small mt-1">{{ errors.thresholdsText }}</div>
                  </div>

                  <div class="col-md-3 form-group">
                    <label class="ai-settings__label">
                      WEB SEARCH
                      <i
                        v-b-tooltip.hover
                        class="fa fa-info-circle ai-settings__info"
                        title="Lets the assistant look things up on the web. Not available yet — the switch exists so turning it on later is a setting rather than a release."
                      ></i>
                    </label>
                    <div class="custom-switch success-handle mt-1">
                      <input
                        id="cfg-web-search"
                        v-model="form.webSearchEnabled"
                        type="checkbox"
                        class="custom-switch-input"
                      />
                      <label class="custom-switch-label" for="cfg-web-search">
                        <span class="custom-switch-text1">On</span>
                        <span class="custom-switch-text2">Off</span>
                      </label>
                    </div>
                  </div>
                </div>

                <!-- At the foot of the form, where the eye arrives having read it, rather
                     than in a header above the first field. -->
                <div v-if="config && !configLoading" class="ai-settings__actions">
                  <b-button variant="primary" size="sm" class="mr-2" :disabled="!isDirty || savingConfig" @click="saveConfig">
                    <i v-if="savingConfig" class="fa fa-spinner fa-spin mr-1"></i>
                    Save changes
                  </b-button>
                  <b-button variant="outline-secondary" size="sm" :disabled="!isDirty || savingConfig" @click="resetForm">
                    Discard changes
                  </b-button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </b-tab>

      <b-tab v-if="canManageSkills" title="Skills" :active="!canManageConfig">
        <!-- ── Skills ───────────────────────────────────────────────────────────────
             What the assistant is told to be, as opposed to what it costs. Drafts and
             archived Skills are listed here and nowhere else: the picker serves published,
             non-archived Skills only, so a Skill someone is halfway through writing is
             invisible to users and must not be invisible to its author. -->
        <div class="row clearfix">
          <div class="col-lg-12">
            <div class="card card-top-line">
              <div class="header d-flex justify-content-between align-items-center">
                <h2>Skills</h2>
                <div class="ml-auto d-flex align-items-center">
                  <b-button variant="primary" size="sm" @click="openCreateSkill">
                    <i class="fa fa-plus mr-1"></i> New Skill
                  </b-button>
                </div>
              </div>

              <div class="body pt-0">
                <b-table
                  responsive
                  table-class="mb-0 table-custom border"
                  head-variant="light"
                  :items="skills"
                  :fields="skillFields"
                  :busy="skillsLoading"
                  striped
                  show-empty
                  empty-text="No Skills yet."
                >
                  <template #table-busy>
                    <div class="text-center text-muted py-3">
                      <i class="fa fa-spinner fa-spin mr-1"></i> Loading Skills…
                    </div>
                  </template>

                  <template #head(liveVersion)="data">
                    {{ data.label }}
                    <i
                      v-b-tooltip.hover
                      class="fa fa-info-circle ai-settings__info"
                      title="The version actually serving messages. Clients choose a Skill, never a version — the live one is resolved when each message is sent, so publishing an improvement reaches everyone on their next message."
                    ></i>
                  </template>

                  <template #cell(name)="data">
                    <span class="font-weight-bold">{{ data.value }}</span>
                    <!-- The fallback for every new conversation. Named here because it is
                         the one row whose archive control is missing, and a control that
                         is simply absent invites a bug report. -->
                    <i
                      v-if="data.item.isProtected"
                      v-b-tooltip.hover
                      class="fa fa-shield ai-settings__info ml-1"
                      title="The fallback Skill for new conversations. Its text and name can be changed freely, but it cannot be archived."
                    ></i>
                    <div class="ai-settings__slug">{{ data.item.slug }}</div>
                  </template>

                  <template #cell(status)="data">
                    <span class="ai-settings__skill-pill" :class="`is-${data.value}`">{{ data.value }}</span>
                  </template>

                  <!-- The column that says whether this Skill can be offered to anyone at
                       all. A Skill with four drafts and nothing published looks busy in
                       every other column and serves nobody. -->
                  <template #cell(liveVersion)="data">
                    <template v-if="data.item.publishedVersion">
                      v{{ data.item.publishedVersion.versionNumber }}
                    </template>
                    <span v-else class="ai-settings__denied">
                      <i class="fa fa-exclamation-circle mr-1"></i>None
                    </span>
                  </template>

                  <template #cell(versionCount)="data">
                    <span class="text-muted">{{ data.value }}</span>
                  </template>

                  <template #cell(action)="data">
                    <b-button
                      v-b-tooltip.hover
                      variant="link"
                      size="sm"
                      class="p-0 mr-3"
                      title="Edit"
                      @click="openEditSkill(data.item)"
                    >
                      <i class="fa fa-pencil"></i>
                    </b-button>
                    <!-- No delete anywhere on this screen. Archiving is how a Skill is
                         retired, because the conversations that used it have to stay
                         readable — which means the row has to survive. -->
                    <b-button
                      v-if="data.item.status !== 'archived' && !data.item.isProtected"
                      v-b-tooltip.hover
                      variant="link"
                      size="sm"
                      class="p-0 text-danger"
                      title="Archive"
                      @click="confirmArchiveSkill(data.item)"
                    >
                      <i class="fa fa-archive"></i>
                    </b-button>
                    <b-button
                      v-else-if="data.item.status === 'archived'"
                      v-b-tooltip.hover
                      variant="link"
                      size="sm"
                      class="p-0 text-success"
                      title="Restore"
                      @click="restoreSkill(data.item)"
                    >
                      <i class="fa fa-undo"></i>
                    </b-button>
                  </template>
                </b-table>
              </div>
            </div>
          </div>
        </div>
      </b-tab>

      <b-tab v-if="canManageConfig" title="Quota Profiles">
        <!-- ── Quota profiles ───────────────────────────────────────────────────────── -->
        <div class="row clearfix">
          <div class="col-lg-12">
            <div class="card card-top-line">
              <div class="header d-flex justify-content-between align-items-center">
                <h2>Quota Profiles</h2>
                <div class="ml-auto d-flex align-items-center">
                  <b-button variant="primary" size="sm" @click="openCreateProfile">
                    <i class="fa fa-plus mr-1"></i> New profile
                  </b-button>
                </div>
              </div>

              <div class="body pt-0">
                <b-table
                  responsive
                  table-class="mb-0 table-custom border"
                  head-variant="light"
                  :items="profiles"
                  :fields="profileFields"
                  :busy="profilesLoading"
                  striped
                  show-empty
                  empty-text="No quota profiles yet. Every role is denied until one exists and is assigned."
                >
                  <template #table-busy>
                    <div class="text-center text-muted py-3">
                      <i class="fa fa-spinner fa-spin mr-1"></i> Loading profiles…
                    </div>
                  </template>

                  <template #cell(name)="data">
                    <span class="font-weight-bold">{{ data.value }}</span>
                    <span v-if="!data.item.isActive" class="badge badge-secondary ml-2">inactive</span>
                  </template>

                  <!-- Explanations ride the column headers, as they do on the form. -->
                  <template #head(monthlySpend)="data">
                    {{ data.label }}
                    <i
                      v-b-tooltip.hover
                      class="fa fa-info-circle ai-settings__info"
                      title="The most one person carrying this profile can spend in a calendar month. Measured against the token price, so it only binds once that price is set."
                    ></i>
                  </template>

                  <template #cell(monthlySpend)="data">
                    <span v-if="data.item.monthlySpendLimitMicros === null" class="text-muted">—</span>
                    <template v-else>
                      ${{ formatMicros(data.item.monthlySpendLimitMicros) }}
                      <!-- A budget shown as active while it silently does nothing is
                           worse than no budget at all. -->
                      <i
                        v-if="!data.item.spendLimitEnforceable"
                        v-b-tooltip.hover
                        class="fa fa-exclamation-circle ai-settings__denied ml-1"
                        title="This budget cannot bind: the model in use has no rate recorded, so there is nothing to measure spend against. Choose a priced model, or have the rate recorded against this one."
                      ></i>
                    </template>
                  </template>

                  <template #cell(assignedRoles)="data">
                    <span v-if="!data.value || !data.value.length" class="text-muted">—</span>
                    <span v-for="role in data.value" v-else :key="role" class="badge badge-info-solid mr-1">
                      {{ role }}
                    </span>
                  </template>

                  <!-- Icons, with the words on hover: two per row across a short table,
                       and the pencil and the cross are unambiguous enough that spelling
                       them out was costing more width than it bought. -->
                  <template #cell(action)="data">
                    <b-button
                      v-b-tooltip.hover
                      variant="link"
                      size="sm"
                      class="p-0 mr-3"
                      title="Edit"
                      @click="openEditProfile(data.item)"
                    >
                      <i class="fa fa-pencil"></i>
                    </b-button>
                    <b-button
                      v-if="data.item.isActive"
                      v-b-tooltip.hover
                      variant="link"
                      size="sm"
                      class="p-0 text-danger"
                      title="Deactivate"
                      @click="confirmDeactivate(data.item)"
                    >
                      <i class="fa fa-times"></i>
                    </b-button>
                    <b-button
                      v-else
                      v-b-tooltip.hover
                      variant="link"
                      size="sm"
                      class="p-0 text-success"
                      title="Reactivate"
                      @click="setProfileActive(data.item, true)"
                    >
                      <i class="fa fa-undo"></i>
                    </b-button>
                  </template>
                </b-table>
              </div>
            </div>
          </div>
        </div>
      </b-tab>

      <b-tab v-if="canManageConfig" title="Roles">
        <!-- ── Role assignment ──────────────────────────────────────────────────────── -->
        <div class="row clearfix">
          <div class="col-lg-12">
            <div class="card card-top-line">
              <div class="header d-flex justify-content-between align-items-center">
                <h2>Roles</h2>
              </div>

              <div class="body pt-0">
                <!-- Roles carrying no profile are listed rather than filtered out: a role
                     with none is denied, and that denial is invisible everywhere else. -->
                <b-table
                  responsive
                  table-class="mb-0 table-custom border"
                  head-variant="light"
                  :items="roles"
                  :fields="roleFields"
                  :busy="rolesLoading"
                  striped
                  show-empty
                >
                  <template #table-busy>
                    <div class="text-center text-muted py-3">
                      <i class="fa fa-spinner fa-spin mr-1"></i> Loading roles…
                    </div>
                  </template>

                  <template #head(aiChat)="data">
                    {{ data.label }}
                    <i
                      v-b-tooltip.hover
                      class="fa fa-info-circle ai-settings__info"
                      title="Whether people in this role can open the AI workspace at all. Off means the workspace is not offered to them and any attempt to use it is refused."
                    ></i>
                  </template>

                  <template #head(quotaProfileId)="data">
                    {{ data.label }}
                    <i
                      v-b-tooltip.hover
                      class="fa fa-info-circle ai-settings__info"
                      title="The set of limits everyone in this role shares — how much they can use in a month, and how long a single message can be. Which roles carry a profile is what decides who is in the pilot."
                    ></i>
                  </template>

                  <template #cell(roleName)="data">
                    <span class="font-weight-bold">{{ data.value }}</span>
                  </template>

                  <!-- Access and allowance are two separate gates and both have to be
                       open. This one grants `ai.chat.use`; the profile beside it grants
                       something to spend. -->
                  <template #cell(aiChat)="data">
                    <div class="custom-switch success-handle d-flex align-items-center">
                      <input
                        :id="`ai-chat-${data.item.roleId}`"
                        type="checkbox"
                        class="custom-switch-input"
                        :checked="data.item.hasChatPermission"
                        :disabled="savingChatRoleId === data.item.roleId"
                        @change="toggleChatPermission(data.item, $event)"
                      />
                      <label class="custom-switch-label" :for="`ai-chat-${data.item.roleId}`">
                        <span class="custom-switch-text1">On</span>
                        <span class="custom-switch-text2">Off</span>
                      </label>
                      <i
                        v-if="savingChatRoleId === data.item.roleId"
                        class="fa fa-spinner fa-spin ai-settings__pending-icon"
                      ></i>
                    </div>
                  </template>

                  <template #cell(quotaProfileId)="data">
                    <div class="d-flex align-items-center">
                    <!-- Keyed on the profile list. Roles load before profiles do, so the
                         select was being given its value while it still had a single
                         option; when the options arrived the browser reset it to the first
                         one, and Vue saw no change to re-apply. The key rebuilds it once
                         the options exist. -->
                    <select
                      :key="`role-${data.item.roleId}-${profiles.length}`"
                      class="form-control form-control-sm ai-settings__role-select"
                      :value="data.value === null || data.value === undefined ? '' : String(data.value)"
                      :disabled="savingRoleId === data.item.roleId"
                      @change="assignProfile(data.item, $event.target.value)"
                    >
                      <option value="">None</option>
                      <option v-for="profile in profiles" :key="profile.id" :value="profile.id" :disabled="!profile.isActive">
                        {{ profile.name }}{{ profile.isActive ? "" : " (inactive)" }}
                      </option>
                    </select>

                    <!-- The consequence, next to the cause. A role with no profile looks
                         perfectly configured otherwise, and this is the only place that
                         says why its messages are refused. -->
                    <i
                      v-if="savingRoleId === data.item.roleId"
                      class="fa fa-spinner fa-spin ai-settings__pending-icon ml-2"
                    ></i>
                    <i
                      v-else-if="!data.item.quotaProfileId"
                      v-b-tooltip.hover
                      class="fa fa-exclamation-circle ai-settings__denied ml-2"
                      title="A quota profile hasn't been granted to this role. Without a quota profile, a role has no tokens to spend and therefore cannot use the chat."
                    ></i>
                    <i
                      v-else-if="inactiveProfileIds.includes(data.item.quotaProfileId)"
                      v-b-tooltip.hover
                      class="fa fa-exclamation-circle ai-settings__denied ml-2"
                      title="This profile has been deactivated, which denies the role exactly as having no profile would. Reactivate it, or choose another."
                    ></i>
                    </div>
                  </template>

                </b-table>
              </div>
            </div>
          </div>
        </div>
      </b-tab>
    </b-tabs>

    <SkillModal
      :visible="skillModalOpen"
      :skill="editingSkill"
      :can-publish="canPublishSkills"
      :max-context-tokens="config ? config.maxContextTokens : null"
      @close="skillModalOpen = false"
      @saved="onSkillSaved"
      @archive="archiveFromModal"
      @restore="restoreFromModal"
    />

    <QuotaProfileModal
      :visible="profileModalOpen"
      :profile="editingProfile"
      :model-is-priced="modelIsPriced"
      @close="profileModalOpen = false"
      @saved="onProfileSaved"
      @deactivate="deactivateFromModal"
      @reactivate="reactivateFromModal"
    />
  </div>
</template>

<script>
import AiAdminService from "@/services/aiAdminService";
import { myFunction } from "@/common/common-helper.js";
import { describeApiError } from "@/utils/ai/apiError";
import {
  changedFields,
  describeAvailability,
  formatMicros,
  formatNumber,
  formatThresholds,
  microsToUnits,
  parseThresholds,
  unitsToMicros,
} from "@/utils/ai/settings";
import AiSkillAdminService from "@/services/aiSkillAdminService";
import QuotaProfileModal from "./QuotaProfileModal.vue";
import SkillModal from "./SkillModal.vue";

export default {
  name: "AiSettingsPanel",

  components: {
    QuotaProfileModal,
    SkillModal,
  },

  data() {
    return {
      config: null,
      // Mirrors `config.chatEnabled` rather than binding to it directly. The switch is
      // applied on the click, so a failed request has to be able to put it back — and a
      // checkbox bound to a value that never changed would stay visually flipped.
      chatEnabledSwitch: false,
      models: [],
      profiles: [],
      roles: [],
      skills: [],

      configLoading: true,
      profilesLoading: true,
      rolesLoading: true,
      skillsLoading: true,
      savingConfig: false,
      togglingChat: false,
      savingRoleId: null,
      savingChatRoleId: null,
      loadError: null,

      form: emptyForm(),
      errors: {},

      profileModalOpen: false,
      editingProfile: null,

      skillModalOpen: false,
      editingSkill: null,

      // Local to this screen and deliberately unsaved — see the card's own comment.

      // Requests per minute, message length, response tokens and the generation timeout
      // are all still enforced on every message — they are just not decisions anyone
      // makes from this table, and as columns they crowded out the two numbers that
      // actually cost money.
      // Token counts are not shown. They are still enforced underneath as a backstop,
      // but nobody outside engineering reads a token count as a quantity, and the same
      // count means different money on different models — so the budget is the number
      // the decision is actually made in.
      profileFields: [
        { key: "name", label: "PROFILE" },
        { key: "monthlySpend", label: "MONTHLY SPEND" },
        { key: "assignedRoles", label: "ROLES" },
        { key: "action", label: "" },
      ],
      roleFields: [
        { key: "roleName", label: "ROLE" },
        { key: "aiChat", label: "AI CHAT" },
        { key: "quotaProfileId", label: "QUOTA PROFILE" },
      ],
      skillFields: [
        { key: "name", label: "SKILL" },
        { key: "status", label: "STATUS" },
        { key: "liveVersion", label: "LIVE VERSION" },
        { key: "versionCount", label: "VERSIONS" },
        { key: "action", label: "" },
      ],
    };
  },

  computed: {
    /**
     * The three grants this screen spans, and they are deliberately not the same people.
     *
     * `ai.config.manage` is money and the kill switch. `ai.skill.manage` is what the
     * assistant is told to be — an authoring job, which someone can hold without being
     * trusted with a spend ceiling. `ai.skill.publish` is narrower still, because
     * publishing takes effect on everyone's next message with no review step in between.
     *
     * The route admits any of them, so a tab whose calls would 403 is not rendered at
     * all. Hiding it is a courtesy rather than access control — the server refuses these
     * routes regardless — but a screen full of red banners is not a permission boundary,
     * it is just a broken screen.
     */
    canManageConfig() {
      return this.$store.getters["authpermission/hasPermission"]("ai.config.manage");
    },

    canManageSkills() {
      return this.$store.getters["authpermission/hasPermission"]("ai.skill.manage");
    },

    canPublishSkills() {
      return this.$store.getters["authpermission/hasPermission"]("ai.skill.publish");
    },

    /** Either switch being off means off; this is the sentence that says which. */
    availability() {
      return describeAvailability(this.config);
    },

    /**
     * The state, in one word, for the header.
     *
     * Three states rather than two, because "off" hides the distinction that decides who
     * fixes it. Unavailable is someone here having switched it off — one click back.
     * Engineering block is `AI_CHAT_ENABLED` being false, which no one holding
     * `ai.config.manage` can undo from this screen; it takes a secret edit and a pod
     * restart. Painting that red beside a switch that looks like the remedy would send
     * an administrator round a loop they cannot win.
     */
    statusPill() {
      if (!this.config) {
        return { label: "Checking…", variant: "is-unknown" };
      }

      const blockedBy = this.availability.blockedBy;
      if (blockedBy === "environment" || blockedBy === "both") {
        return { label: "Engineering block", variant: "is-engineering" };
      }

      return this.availability.on
        ? { label: "Available", variant: "is-available" }
        : { label: "Unavailable", variant: "is-unavailable" };
    },

    /** The config as loaded, in the shape the PATCH takes. The baseline for the diff. */
    baseline() {
      if (!this.config) {
        return {};
      }
      return {
        defaultModelId: this.config.defaultModelId,
        maxContextTurns: this.config.maxContextTurns,
        maxContextTokens: this.config.maxContextTokens,
        maxOutputTokens: this.config.maxOutputTokens,
        summaryTriggerTokens: this.config.summaryTriggerTokens,
        orgSpendCeilingMicros: this.config.orgSpendCeilingMicros,
        spendAlertThresholds: this.config.spendAlertThresholds || [],
        webSearchEnabled: this.config.webSearchEnabled,
      };
    },

    /** The form in the same shape, so the two can be diffed field by field. */
    edited() {
      return {
        defaultModelId: this.form.defaultModelId,
        maxContextTurns: this.form.maxContextTurns,
        maxContextTokens: this.form.maxContextTokens,
        maxOutputTokens: this.form.maxOutputTokens,
        summaryTriggerTokens: this.form.summaryTriggerTokens,
        orgSpendCeilingMicros: unitsToMicros(this.form.spendCeilingUnits),
        spendAlertThresholds: parseThresholds(this.form.thresholdsText) || [],
        webSearchEnabled: this.form.webSearchEnabled,
      };
    },

    pendingChanges() {
      return this.config ? changedFields(this.baseline, this.edited) : {};
    },

    isDirty() {
      return Object.keys(this.pendingChanges).length > 0;
    },

    /**
     * The model currently chosen in the form, with its rates.
     *
     * Prices belong to the model and are recorded from the provider's price list — there
     * is no rate on the config to fall back to, and nothing here writes one.
     */
    selectedModel() {
      return this.models.find((model) => model.modelId === this.form.defaultModelId) || null;
    },

    /**
     * Whether spend can be measured at all for the chosen model.
     *
     * Everything in the interface that turns tokens into money depends on this, and the
     * backend applies the same test when it decides whether a profile's spend limit can
     * bind.
     */
    modelIsPriced() {
      return Boolean(this.selectedModel && this.selectedModel.isPriced);
    },

    currentModelDisabled() {
      if (!this.config || !this.models.length) {
        return false;
      }
      const current = this.models.find((model) => model.modelId === this.config.defaultModelId);
      return Boolean(current) && !current.isEnabled;
    },

    inactiveProfileIds() {
      return this.profiles.filter((profile) => !profile.isActive).map((profile) => profile.id);
    },
  },

  created() {
    this.loadAll();
  },

  methods: {
    formatNumber,
    formatMicros,

    async loadAll() {
      this.loadError = null;

      // Only what this user may actually read. A skills-only author reaching this screen
      // would otherwise fire four requests that all 403 and land on a red banner saying
      // the settings could not be loaded — which is true, and is not their problem.
      const loads = [];

      if (this.canManageConfig) {
        loads.push(this.loadConfig(), this.loadModels(), this.loadProfiles(), this.loadRoles());
      } else {
        this.configLoading = false;
        this.profilesLoading = false;
        this.rolesLoading = false;
      }

      if (this.canManageSkills) {
        loads.push(this.loadSkills());
      } else {
        this.skillsLoading = false;
      }

      await Promise.all(loads);
    },

    async loadConfig() {
      this.configLoading = true;
      try {
        const response = await AiAdminService.getConfig();
        this.config = response.data;
        this.chatEnabledSwitch = this.config.chatEnabled;
        this.resetForm();
      } catch (error) {
        this.loadError = describeApiError(error, "AI Workspace settings could not be loaded.");
      } finally {
        this.configLoading = false;
      }
    },

    async loadModels() {
      try {
        const response = await AiAdminService.getModels();
        this.models = response.data || [];
      } catch (error) {
        this.loadError = describeApiError(error, "The model allowlist could not be loaded.");
      }
    },

    async loadProfiles() {
      this.profilesLoading = true;
      try {
        const response = await AiAdminService.getQuotaProfiles();
        this.profiles = response.data || [];
      } catch (error) {
        this.loadError = describeApiError(error, "Quota profiles could not be loaded.");
      } finally {
        this.profilesLoading = false;
      }
    },

    async loadRoles() {
      this.rolesLoading = true;
      try {
        const response = await AiAdminService.getRoleAssignments();
        this.roles = response.data || [];
      } catch (error) {
        this.loadError = describeApiError(error, "Role assignments could not be loaded.");
      } finally {
        this.rolesLoading = false;
      }
    },

    async loadSkills() {
      this.skillsLoading = true;
      try {
        const response = await AiSkillAdminService.getSkills();
        this.skills = response.data || [];
      } catch (error) {
        this.loadError = describeApiError(error, "Skills could not be loaded.");
      } finally {
        this.skillsLoading = false;
      }
    },

    openCreateSkill() {
      this.editingSkill = null;
      this.skillModalOpen = true;
    },

    openEditSkill(skill) {
      this.editingSkill = skill;
      this.skillModalOpen = true;
    },

    /**
     * A write landed inside the modal.
     *
     * The list is re-read rather than patched in place, unlike the roles and profiles
     * tables. Publishing moves two rows at once — the new version becomes live and the
     * old one is archived in the same transaction — and the summary this table shows is
     * derived from both, so reassembling it here would be reimplementing the transaction
     * in the client.
     *
     * `keepOpen` distinguishes saving from finishing. Creating a Skill closes the modal;
     * saving a draft or publishing one is a step in the middle of the job, and closing
     * the modal under someone mid-edit is its own bug.
     */
    onSkillSaved(event) {
      if (!event.keepOpen) {
        this.skillModalOpen = false;
      }

      const messages = {
        created: `Skill "${event.skill.name}" created as a draft. Publish a version to offer it.`,
        saved: `Skill "${event.skill.name}" saved.`,
        draft: "Draft saved. It is not live until it is published.",
        published: `Version ${event.versionNumber} of "${event.skill.name}" is live. Everyone gets it on their next message.`,
      };

      myFunction.showInfo(messages[event.action] || "Saved.", "success");

      // Created, but the publish that was meant to follow it did not land. The Skill is
      // a draft and the list will say so; what it will not say is that anything went
      // wrong, and the author asked for it to be live.
      if (event.publishError) {
        myFunction.showInfo(
          `"${event.skill.name}" was created but not published. ${event.publishError} ` +
            "Open it and publish the draft.",
          "error",
        );
      }

      this.loadSkills();
    },

    /**
     * Archiving from inside the edit modal.
     *
     * The modal closes first so the confirmation is not a dialog on top of a dialog, and
     * declining costs nothing — the form reloads from the Skill when it is reopened.
     */
    archiveFromModal(skill) {
      this.skillModalOpen = false;
      this.confirmArchiveSkill(skill);
    },

    restoreFromModal(skill) {
      this.skillModalOpen = false;
      this.restoreSkill(skill);
    },

    confirmArchiveSkill(skill) {
      this.$swal
        .mixin({
          customClass: {
            confirmButton: "btn btn-primary btn-lg my-3",
            cancelButton: "btn btn-outline-secondary btn-lg",
          },
          buttonsStyling: false,
        })
        .fire({
          title: `Archive ${skill.name}?`,
          // What archiving does and, just as importantly, what it does not: there is no
          // delete here, and the history is the reason.
          text:
            "It stops being offered to anyone. Conversations that already used it stay " +
            "readable, and it can be restored later.",
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: "Archive",
          cancelButtonText: "Cancel",
        })
        .then((result) => {
          if (result.isConfirmed) {
            this.setSkillStatus(skill, "archived");
          }
        });
    },

    /**
     * Restoring is its own decision, which is why it is its own control.
     *
     * Publishing new text into an archived Skill does not bring it back — the backend
     * leaves it archived on purpose, because deciding the text is ready is not the same
     * as deciding the Skill should be offered again.
     */
    restoreSkill(skill) {
      this.setSkillStatus(skill, "published");
    },

    async setSkillStatus(skill, status) {
      try {
        await AiSkillAdminService.updateSkill(skill.id, { status });
        myFunction.showInfo(
          status === "archived"
            ? `"${skill.name}" archived. It is no longer offered.`
            : `"${skill.name}" restored.`,
          "success",
        );
        this.loadSkills();
      } catch (error) {
        const described = describeApiError(error, "That Skill could not be changed.");
        myFunction.showInfo(described.message, "error");
        // The refusal may be the protected-Skill rule, in which case the row is not what
        // this screen thought it was and is worth re-reading.
        this.loadSkills();
      }
    },

    /**
     * Grant or remove `ai.chat.use` for a role.
     *
     * One row, written by a route that exists for exactly this. The switch moves on the
     * click and goes back if the write is refused; the roles list carries the state, so
     * nothing else has to be re-read.
     */
    async toggleChatPermission(role, event) {
      const wanted = event.target.checked;

      this.applyChatPermission(role.roleId, wanted);
      this.savingChatRoleId = role.roleId;

      try {
        await AiAdminService.setRoleChatAccess(role.roleId, wanted);
        myFunction.showInfo(
          wanted
            ? `${role.roleName} can now use AI chat.`
            : `${role.roleName} can no longer use AI chat.`,
          "success",
        );
      } catch (error) {
        const described = describeApiError(error, "That role could not be changed.");
        myFunction.showInfo(described.message, "error");
        this.applyChatPermission(role.roleId, !wanted);
      } finally {
        this.savingChatRoleId = null;
      }
    },

    applyChatPermission(roleId, hasChatPermission) {
      this.roles = this.roles.map((row) =>
        row.roleId === roleId ? { ...row, hasChatPermission } : row,
      );
    },

    resetForm() {
      if (!this.config) {
        this.form = emptyForm();
        return;
      }

      this.errors = {};
      this.form = {
        defaultModelId: this.config.defaultModelId,
        maxContextTurns: this.config.maxContextTurns,
        maxContextTokens: this.config.maxContextTokens,
        maxOutputTokens: this.config.maxOutputTokens,
        summaryTriggerTokens: this.config.summaryTriggerTokens,
        spendCeilingUnits: microsToUnits(this.config.orgSpendCeilingMicros),
        thresholdsText: formatThresholds(this.config.spendAlertThresholds),
        webSearchEnabled: this.config.webSearchEnabled,
      };
    },

    /**
     * The switch was clicked. Turning it off asks first; turning it on does not.
     *
     * The confirmation is asymmetric on purpose. Switching off takes the workspace away
     * from everyone at once, and the person clicking may not have pictured the blast
     * radius; switching on restores it, which is not a decision anyone regrets.
     *
     * Turning on, the switch stays where the click put it and the request goes out
     * behind it. Turning off, it is put straight back until the question is answered —
     * while a confirmation is open nothing has been decided, and a switch already
     * sitting in the off position would say otherwise.
     */
    onSwitchToggled(event) {
      const wanted = event.target.checked;

      if (wanted === this.chatEnabledSwitch) {
        return;
      }

      if (wanted) {
        this.setChatEnabled(true);
        return;
      }

      event.target.checked = this.chatEnabledSwitch;

      this.$swal
        .mixin({
          customClass: {
            confirmButton: "btn btn-danger btn-lg my-3",
            cancelButton: "btn btn-outline-secondary btn-lg",
          },
          buttonsStyling: false,
        })
        .fire({
          title: "Turn off AI chat?",
          text:
            "This turns AI chat off for every user. Nobody will be able to send a " +
            "message until it is switched back on. Conversations already saved stay " +
            "readable. Are you sure?",
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: "Yes, turn it off",
          cancelButtonText: "Cancel",
        })
        .then((result) => {
          if (result.isConfirmed) {
            this.setChatEnabled(false);
          }
        });
    },

    /**
     * The kill switch itself.
     *
     * Not part of the form below and not behind a Save: it is sent on its own, so
     * turning the workspace off can never carry a half-edited token budget along with it.
     */
    async setChatEnabled(enabled) {
      if (!this.config || this.togglingChat) {
        return;
      }

      // The switch moves now, not when the round trip finishes. A control that sits
      // still for a second after a deliberate click reads as broken, and the click gets
      // repeated. The spinner beside it says the request is still out, and a refusal
      // puts the switch back — so the only window where it shows something unconfirmed
      // is the one where it is visibly still working.
      const previous = this.chatEnabledSwitch;
      this.chatEnabledSwitch = enabled;
      this.togglingChat = true;

      try {
        const response = await AiAdminService.updateConfig({ chatEnabled: enabled });
        this.config = response.data;
        this.chatEnabledSwitch = this.config.chatEnabled;
        myFunction.showInfo(
          enabled ? "AI workspace switched on." : "AI workspace switched off.",
          "success",
        );

        // The engineering backstop may be the one holding it off, in which case the
        // switch just moved and nothing else did. Say so rather than leaving the
        // administrator to work out why users still cannot send.
        if (enabled && !this.config.chatEnabledByEnvironment) {
          myFunction.showInfo(
            "Engineering still has the workspace blocked, so it stays off for users.",
            "warning",
          );
        }
      } catch (error) {
        const described = describeApiError(error, "That switch could not be changed.");
        myFunction.showInfo(described.message, "error");
        // Back where it was. A kill switch that looks flipped but is not is worse than
        // one that refused to move.
        this.chatEnabledSwitch = previous;
      } finally {
        this.togglingChat = false;
      }
    },

    validateConfig() {
      const errors = {};
      const form = this.form;

      if (!inRange(form.maxContextTokens, 1000, 1000000)) {
        errors.maxContextTokens = "Between 1,000 and 1,000,000.";
      }
      if (!inRange(form.maxContextTurns, 1, 500)) {
        errors.maxContextTurns = "Between 1 and 500.";
      }
      if (!inRange(form.maxOutputTokens, 256, 128000)) {
        errors.maxOutputTokens = "Between 256 and 128,000.";
      }
      if (!inRange(form.summaryTriggerTokens, 1000, 500000)) {
        errors.summaryTriggerTokens = "Between 1,000 and 500,000.";
      }
      if (form.spendCeilingUnits !== null && form.spendCeilingUnits !== "" && Number(form.spendCeilingUnits) < 0) {
        errors.spendCeilingUnits = "Zero or more, or empty for no ceiling.";
      }
      if (parseThresholds(form.thresholdsText) === null) {
        errors.thresholdsText = "Percentages between 0 and 100, separated by commas.";
      }

      this.errors = errors;
      return !Object.keys(errors).length;
    },

    async saveConfig() {
      if (!this.validateConfig()) {
        return;
      }

      const changes = this.pendingChanges;
      if (!Object.keys(changes).length) {
        return;
      }

      this.savingConfig = true;
      try {
        const response = await AiAdminService.updateConfig(changes);
        this.config = response.data;
        this.resetForm();
        myFunction.showInfo("Settings saved.", "success");

        // Profile rows are derived from these two: the output cap decides what a
        // profile's response limit really is, and the token price decides whether its
        // spend limit can bind at all.
        if (changes.maxOutputTokens !== undefined) {
          this.loadProfiles();
        }
      } catch (error) {
        const described = describeApiError(error, "Those settings could not be saved.");
        myFunction.showInfo(described.message, "error");
      } finally {
        this.savingConfig = false;
      }
    },

    openCreateProfile() {
      this.editingProfile = null;
      this.profileModalOpen = true;
    },

    openEditProfile(profile) {
      this.editingProfile = profile;
      this.profileModalOpen = true;
    },

    onProfileSaved(profile) {
      this.profileModalOpen = false;
      myFunction.showInfo(`Profile "${profile.name}" saved.`, "success");
      // Both lists move together: a rename shows up beside every role carrying it, and
      // a new profile has to appear in the role selects before it can be assigned.
      this.loadProfiles();
      this.loadRoles();
    },

    /**
     * Deactivating from inside the edit modal.
     *
     * The modal closes first, so the confirmation is not a dialog on top of a dialog —
     * and if the question is declined nothing has been lost, since the form reloads from
     * the profile when it is reopened.
     */
    deactivateFromModal(profile) {
      this.profileModalOpen = false;
      this.confirmDeactivate(profile);
    },

    reactivateFromModal(profile) {
      this.profileModalOpen = false;
      this.setProfileActive(profile, true);
    },

    confirmDeactivate(profile) {
      const carriers = profile.assignedRoles || [];
      const swalWithBootstrapButtons = this.$swal.mixin({
        customClass: {
          confirmButton: "btn btn-primary btn-lg my-3",
          cancelButton: "btn btn-outline-secondary btn-lg",
        },
        buttonsStyling: false,
      });

      swalWithBootstrapButtons
        .fire({
          title: `Deactivate ${profile.name}?`,
          // Deactivating denies every role carrying it, which is not obvious from a
          // switch labelled "active" — so the roles are named in the question.
          text: carriers.length
            ? `Sends will be refused for ${carriers.join(", ")} until this profile is reactivated or those roles are given another one.`
            : "No role carries this profile, so nothing is denied by deactivating it.",
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: "Deactivate",
        })
        .then((result) => {
          if (result.isConfirmed) {
            this.setProfileActive(profile, false);
          }
        });
    },

    async setProfileActive(profile, isActive) {
      try {
        await AiAdminService.updateQuotaProfile(profile.id, { isActive });
        myFunction.showInfo(
          isActive ? `Profile "${profile.name}" reactivated.` : `Profile "${profile.name}" deactivated.`,
          "success",
        );
        this.loadProfiles();
        this.loadRoles();
      } catch (error) {
        const described = describeApiError(error, "That profile could not be changed.");
        myFunction.showInfo(described.message, "error");
      }
    },

    /**
     * Move a role onto a profile in the data already loaded.
     *
     * The roles table holds the assignment and the profiles table holds the roles that
     * carry it, so both have to move together — but neither has to be re-read. Anything
     * the server derives from this and the client cannot (nothing today) would be the
     * reason to refetch instead.
     */
    applyRoleAssignment(role, quotaProfileId) {
      const profile = this.profiles.find((candidate) => candidate.id === quotaProfileId) || null;

      this.roles = this.roles.map((row) =>
        row.roleId === role.roleId
          ? {
              ...row,
              quotaProfileId,
              quotaProfileName: profile ? profile.name : null,
            }
          : row,
      );

      this.profiles = this.profiles.map((candidate) => {
        const without = (candidate.assignedRoles || []).filter((name) => name !== role.roleName);
        const carries = candidate.id === quotaProfileId;

        return {
          ...candidate,
          assignedRoles: carries ? [...without, role.roleName] : without,
        };
      });
    },

    async assignProfile(role, rawValue) {
      const quotaProfileId = rawValue === "" || rawValue === "null" ? null : Number(rawValue);
      if (quotaProfileId === role.quotaProfileId) {
        return;
      }

      this.savingRoleId = role.roleId;
      try {
        await AiAdminService.setRoleQuotaProfile(role.roleId, quotaProfileId);
        myFunction.showInfo(
          quotaProfileId === null
            ? `${role.roleName} no longer has a quota profile — its sends will be refused.`
            : `${role.roleName} assigned a quota profile.`,
          "success",
        );
        // Both tables are patched in place rather than refetched. The server has already
        // said the change took; refetching would put every table back into its loading
        // state and make the whole panel blink for a change to one cell.
        this.applyRoleAssignment(role, quotaProfileId);
      } catch (error) {
        const described = describeApiError(error, "That role could not be changed.");
        myFunction.showInfo(described.message, "error");
        // Only on failure is a reload warranted: the row now shows whatever the server
        // has, which may not be either value involved in this click.
        this.loadRoles();
      } finally {
        this.savingRoleId = null;
      }
    },
  },
};

function emptyForm() {
  return {
    defaultModelId: "",
    maxContextTurns: null,
    maxContextTokens: null,
    maxOutputTokens: null,
    summaryTriggerTokens: null,
    spendCeilingUnits: null,
    thresholdsText: "",
    webSearchEnabled: false,
  };
}

function inRange(value, min, max) {
  return Number.isFinite(value) && Number.isInteger(value) && value >= min && value <= max;
}
</script>

<style scoped>
/* The default padding leaves the spinner floating in the middle of the field's right
   edge; this puts it against the border where it belongs. */
.ai-settings input[type="number"] {
  padding-right: 2px;
}

.ai-settings input[type="number"]::-webkit-inner-spin-button,
.ai-settings input[type="number"]::-webkit-outer-spin-button {
  margin: 0;
  height: 100%;
  opacity: 1;
}

.ai-settings__price {
  width: 110px;
}

.ai-settings__label {
  display: block;
  /* Two lines' worth, so a long label wraps without pushing its input below the
     fields beside it. */
  min-height: 34px;
  margin-bottom: 2px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.06em;
  color: #5d6474;
}

.ai-settings__info {
  margin-left: 4px;
  color: #a8afbd;
  cursor: help;
}

.ai-settings__info:hover {
  color: #6eb644;
}

.ai-settings__pending {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-left: 10px;
  font-size: 12px;
  color: #8a91a0;
}

.ai-settings__pending-icon {
  margin-left: 8px;
  color: #8a91a0;
}

.ai-settings__denied {
  color: #dc3545;
  cursor: help;
}

.ai-settings__actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 18px;
  padding-top: 14px;
  border-top: 1px solid #eef0f4;
}

.ai-settings__down {
  margin: 12px 0 0;
  padding: 9px 12px;
  border: 1px solid #f0d6b8;
  border-radius: 8px;
  background: #fdf6ec;
  font-size: 13px;
  color: #8a5a1a;
}

/* The list is short and the names are not long; stretched across the full width of a
   1440px dialog it reads as a mistake. */
.ai-settings__model {
  max-width: 260px;
}

.ai-settings__rate {
  margin-top: 6px;
  font-size: 12px;
  color: #5d6474;
}

.ai-settings__header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 0 4px 14px;
  border-bottom: 1px solid #e6e9ed;
}

.ai-settings__title {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  color: #2b3243;
}

.ai-settings__pill {
  padding: 3px 12px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.02em;
}

.ai-settings__pill.is-available {
  background: rgba(110, 182, 68, 0.14);
  color: #4a7f2c;
}

.ai-settings__pill.is-unavailable {
  background: rgba(220, 53, 69, 0.12);
  color: #b02a37;
}

/* Grey, not red: nobody on this screen can lift it, so treating it as an alarm to act
   on would be pointing at the wrong person. */
.ai-settings__pill.is-engineering {
  background: #eceef2;
  color: #5d6474;
}

.ai-settings__pill.is-unknown {
  background: #f2f4f8;
  color: #8a91a0;
}

.ai-settings__header-switch {
  margin-left: auto;
  display: flex;
  align-items: center;
}

.ai-settings__availability.is-off {
  border-left: 4px solid #dc3545;
}

.ai-settings__availability.is-on {
  border-left: 4px solid #6eb644;
}

.ai-settings__availability-detail {
  font-size: 15px;
  line-height: 1.5;
}

.ai-settings__switch {
  padding: 12px 16px;
  border: 1px solid #e6e9ed;
  border-radius: 6px;
  height: 100%;
}

.ai-settings__role-select {
  max-width: 260px;
}

.ai-settings__slug {
  font-family: "SFMono-Regular", Consolas, "Liberation Mono", Menlo, monospace;
  font-size: 11px;
  color: #8a91a0;
}

.ai-settings__skill-pill {
  display: inline-block;
  padding: 2px 10px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.02em;
}

.ai-settings__skill-pill.is-published {
  background: rgba(110, 182, 68, 0.14);
  color: #4a7f2c;
}

.ai-settings__skill-pill.is-draft {
  background: #eef1f6;
  color: #5d6474;
}

.ai-settings__skill-pill.is-archived {
  background: #f2f4f8;
  color: #8a91a0;
}





</style>
